import { classList } from '@/utils/cn';

import { TranscriptSpeakerLabel } from '../meetings-speaker-rename.components';
import {
  TranscriptBubbleText,
  TranscriptConsentNotice,
} from '../meetings-transcript-display.components';
import { joinSegmentText } from '../meetings-transcript.utils';

import { TranscriptTooltip } from './TranscriptTooltip';

import type { useProcessedTranscripts } from '../meetings-transcript-display.hooks';
import type { TranscriptWordSelection } from '../meetings-transcript-display.utils';
import type { CalendarEventAttendee, MeetingId } from '../meetings.types';
import type { ComponentProps } from 'react';

type Props = Pick<
  ComponentProps<'div'>,
  'onMouseDown' | 'onClick' | 'onDoubleClick' | 'onKeyDown'
> & {
  chunk: ReturnType<typeof useProcessedTranscripts>[number];
  index: number;
  totalCount: number;
  itemIdPrefix: string;
  isSpeakerChange: boolean;
  showSpeaker: boolean;
  isInPopup: boolean;
  isNewLine: boolean;
  isLive: boolean;
  isSelectable: boolean;
  isActive: boolean;
  isSelected: boolean;
  isDeleting: boolean;
  meetingId: MeetingId;
  attendees?: CalendarEventAttendee[] | null;
  speakerLabels: string[];
  canEditSpeakers?: boolean;
  bubbleRef: ComponentProps<'div'>['ref'];
  onSelectWord?: (selection: TranscriptWordSelection) => void;
};

export const TranscriptRow = ({
  chunk,
  index,
  totalCount,
  itemIdPrefix,
  isSpeakerChange,
  showSpeaker,
  isInPopup,
  isNewLine,
  isLive,
  isSelectable,
  isActive,
  isSelected,
  isDeleting,
  meetingId,
  attendees,
  speakerLabels,
  canEditSpeakers = false,
  bubbleRef,
  onMouseDown,
  onClick,
  onDoubleClick,
  onKeyDown,
  onSelectWord,
}: Props) => {
  const { speaker, rawSpeaker, segments, isOthers } = chunk;
  const canCorrect = !!onSelectWord && isSelectable && !isDeleting;
  return (
    <>
      {index === 0 && isInPopup && (
        <TranscriptConsentNotice className="pt-1 pb-3 text-center" />
      )}
      <div
        id={`${itemIdPrefix}-${index}`}
        className={classList(
          'flex mb-1 group/transcript-line',
          isSpeakerChange && 'mt-3',
          isOthers ? 'justify-start' : 'justify-end',
          isInPopup ? 'px-3' : 'px-4',
          isNewLine && 'motion-safe:animate-transcript-bubble-in'
        )}
      >
        <div
          className={classList(
            'max-w-[80%] flex flex-col',
            isOthers ? 'items-start' : 'items-end'
          )}
        >
          {showSpeaker && (
            <TranscriptSpeakerLabel
              meetingId={meetingId}
              displayLabel={speaker ?? ''}
              transcriptLabel={rawSpeaker}
              attendees={attendees}
              speakerLabels={speakerLabels}
              isOthers={isOthers}
              editable={canEditSpeakers}
            />
          )}
          <TranscriptTooltip disabled={!canCorrect}>
            <div
              ref={bubbleRef}
              role="option"
              data-transcript-key={chunk.sourceLineIndex}
              tabIndex={isSelectable && isActive && !isDeleting ? 0 : -1}
              aria-selected={isSelected}
              aria-posinset={index + 1}
              aria-setsize={totalCount}
              aria-disabled={!isSelectable || isDeleting}
              aria-label={`${speaker}: ${joinSegmentText(segments)}`}
              aria-description={
                canCorrect
                  ? 'Press Shift+F10 to choose a word to correct.'
                  : undefined
              }
              aria-keyshortcuts={canCorrect ? 'Shift+F10' : undefined}
              onMouseDown={onMouseDown}
              onClick={onClick}
              onDoubleClick={onDoubleClick}
              onKeyDown={(event) => {
                const isCorrectionShortcut =
                  !event.ctrlKey &&
                  !event.metaKey &&
                  !event.altKey &&
                  (event.key === 'ContextMenu' ||
                    (event.key === 'F10' && event.shiftKey));
                if (
                  canCorrect &&
                  isCorrectionShortcut &&
                  event.target === event.currentTarget &&
                  window.getSelection()?.isCollapsed !== false
                ) {
                  const words = event.currentTarget.querySelector<HTMLElement>(
                    '[data-transcript-correctable]'
                  );
                  if (words) {
                    event.preventDefault();
                    event.stopPropagation();
                    words.focus();
                    return;
                  }
                }
                onKeyDown?.(event);
              }}
              className={classList(
                'w-fit text-foreground leading-normal no-record',
                'px-3.5 select-text cursor-text [overflow-wrap:anywhere]',
                'rounded-xl outline-none focus-visible:ring-2 focus-visible:ring-accent',
                isSelected && 'shadow-[inset_0_0_0_2px_var(--shell-accent)]',
                isOthers
                  ? 'py-2 text-left bg-grayA-2'
                  : 'py-1.5 bg-[color-mix(in_srgb,var(--shell-accent)_30%,transparent)] dark:bg-[color-mix(in_srgb,var(--shell-accent)_45%,transparent)]'
              )}
            >
              <TranscriptBubbleText
                segments={segments}
                isOthers={isOthers}
                isLive={isLive}
                onSelectWord={canCorrect ? onSelectWord : undefined}
              />
            </div>
          </TranscriptTooltip>
        </div>
      </div>
    </>
  );
};
